import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import {
  ThumbDownFilledIcon,
  ThumbDownIcon,
  ThumbUpFilledIcon,
  ThumbUpIcon,
} from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { AiPlanUserOutcome } from 'constants/aiChat';

import { setMessageFeedbackAction } from 'store/aiChat/actions';
import { selectAiChatMessageOutcomeByMessageId } from 'store/aiChat/selectors';

import { AiChatTestIds } from '../../../../AiChat.testIds';

import FeedbackButton from './FeedbackButton';
import { FEEDBACK_BUTTONS_CLASS_NAME, FeedbackButtonLabel } from './FeedbackButtons.constants';
import { containerStyles } from './FeedbackButtons.styles';

const FeedbackButtons = ({ messageId }) => {
  const dispatch = useDispatch();
  const userOutcome = useSelector(state => selectAiChatMessageOutcomeByMessageId(state, messageId));

  const isAccepted = userOutcome === AiPlanUserOutcome.ACCEPTED;
  const isDiscarded = userOutcome === AiPlanUserOutcome.DISCARDED;

  const onThumbsUpClick = useCallback(() => {
    const nextOutcome = isAccepted ? AiPlanUserOutcome.NO_FEEDBACK : AiPlanUserOutcome.ACCEPTED;

    dispatch(setMessageFeedbackAction({ messageId, outcome: nextOutcome }));
  }, [dispatch, messageId, isAccepted]);

  const onThumbsDownClick = useCallback(() => {
    const nextOutcome = isDiscarded ? AiPlanUserOutcome.NO_FEEDBACK : AiPlanUserOutcome.DISCARDED;

    dispatch(setMessageFeedbackAction({ messageId, outcome: nextOutcome }));
  }, [dispatch, messageId, isDiscarded]);

  return (
    <Box
      className={FEEDBACK_BUTTONS_CLASS_NAME}
      data-testid={AiChatTestIds.AI_BOT_MESSAGE_FEEDBACK}
      sx={containerStyles}
    >
      <FeedbackButton
        Icon={isAccepted ? ThumbUpFilledIcon : ThumbUpIcon}
        isFilled={isAccepted}
        isHidden={isDiscarded}
        label={FeedbackButtonLabel.THUMBS_UP}
        testId={AiChatTestIds.AI_BOT_MESSAGE_FEEDBACK_THUMBS_UP}
        onClick={onThumbsUpClick}
      />
      <FeedbackButton
        Icon={isDiscarded ? ThumbDownFilledIcon : ThumbDownIcon}
        isFilled={isDiscarded}
        isHidden={isAccepted}
        label={FeedbackButtonLabel.THUMBS_DOWN}
        testId={AiChatTestIds.AI_BOT_MESSAGE_FEEDBACK_THUMBS_DOWN}
        onClick={onThumbsDownClick}
      />
    </Box>
  );
};

FeedbackButtons.propTypes = {
  messageId: PropTypes.string.isRequired,
};

export default memo(FeedbackButtons);
